import { Box, Heading, Spinner, Tabs, Text, VStack } from '@chakra-ui/react'
import { useCallback, useState } from 'react'

import type { TimeZoneOption } from '@mangovoice/constants'
import type { ToastLike } from '@mangovoice/mango-ui'

import type { SystemSettingsApi } from '../api/systemSettingsApi.types'
import { DefaultsSettingsTab } from '../components/DefaultsSettingsTab'
import { GeneralSettingsTab } from '../components/GeneralSettingsTab'
import { RecordingSettingsTab } from '../components/RecordingSettingsTab'
import {
  useGetExtensions,
  useGetPhoneNumbers,
  useGetPlaylists,
  useGetSystemSettings,
  useGetVirtualFaxMachines,
  useUpdateSystemSettings,
} from '../queries'
import type {
  SystemSettings,
  SystemSettingsReferenceData,
  SystemSettingsTranslateFn,
} from '../types'
import { text } from '../types'

export interface SystemSettingsPageProps {
  api: SystemSettingsApi
  pbxId: number | undefined
  toast: ToastLike
  t?: SystemSettingsTranslateFn
  referenceData?: SystemSettingsReferenceData
  timeZoneOptions?: TimeZoneOption[]
}

export const SystemSettingsPage = ({
  api,
  pbxId,
  toast,
  t,
  referenceData,
  timeZoneOptions,
}: SystemSettingsPageProps) => {
  const [activeTab, setActiveTab] = useState('general')

  const {
    data: settings,
    isLoading,
    isError,
  } = useGetSystemSettings(api, pbxId)

  const updateMutation = useUpdateSystemSettings(api, pbxId)

  const phoneNumbersQuery = useGetPhoneNumbers(api, pbxId)
  const extensionsQuery = useGetExtensions(api, pbxId)
  const virtualFaxQuery = useGetVirtualFaxMachines(api, pbxId)
  const playlistsQuery = useGetPlaylists(api, pbxId)

  const phoneNumbers =
    referenceData?.phoneNumbers ?? phoneNumbersQuery.data ?? []
  const faxNumbers = referenceData?.faxNumbers ?? phoneNumbersQuery.data ?? []
  const extensions = referenceData?.extensions ?? extensionsQuery.data ?? []
  const virtualFaxExtensions =
    referenceData?.virtualFaxExtensions ?? virtualFaxQuery.data ?? []
  const playlists = referenceData?.playlists ?? playlistsQuery.data ?? []

  const isLoadingReferenceData =
    phoneNumbersQuery.isLoading ||
    extensionsQuery.isLoading ||
    virtualFaxQuery.isLoading ||
    playlistsQuery.isLoading

  const handleSave = useCallback(
    async (payload: Partial<SystemSettings>) => {
      try {
        await updateMutation.mutateAsync(payload)
        toast.create({
          title: text(
            t,
            'systemSettings.toast.saveSuccess',
            'Settings saved successfully',
          ),
          type: 'success',
          duration: 3000,
        })
      } catch (error) {
        toast.create({
          title: text(
            t,
            'systemSettings.toast.saveError',
            'Failed to save settings',
          ),
          description: error instanceof Error ? error.message : 'Unknown error',
          type: 'error',
          duration: 5000,
        })
      }
    },
    [updateMutation, toast, t],
  )

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minH="200px"
        flex="1"
      >
        <Spinner size="lg" />
      </Box>
    )
  }

  if (isError || !settings) {
    return (
      <Box p={6}>
        <Text color="fg.error">
          {text(
            t,
            'systemSettings.error.loadFailed',
            'Failed to load system settings. Please try again.',
          )}
        </Text>
      </Box>
    )
  }

  return (
    <Box flex="1" w="full" p={6} overflow="auto">
      <VStack gap={6} align="stretch">
        <Heading fontSize="2xl">
          {text(t, 'systemSettings.title', 'System Settings')}
        </Heading>

        <Tabs.Root
          value={activeTab}
          onValueChange={(details) => setActiveTab(details.value)}
          variant="line"
        >
          <Tabs.List>
            <Tabs.Trigger value="general">
              {text(t, 'systemSettings.tabs.general', 'General')}
            </Tabs.Trigger>
            <Tabs.Trigger value="defaults">
              {text(t, 'systemSettings.tabs.defaults', 'Defaults')}
            </Tabs.Trigger>
            <Tabs.Trigger value="recording">
              {text(t, 'systemSettings.tabs.recording', 'Call Recording')}
            </Tabs.Trigger>
          </Tabs.List>

          <Box mt={6}>
            <Tabs.Content value="general">
              <GeneralSettingsTab
                settings={settings}
                onSave={handleSave}
                isSaving={updateMutation.isPending}
                t={t}
                timeZoneOptions={timeZoneOptions}
              />
            </Tabs.Content>

            <Tabs.Content value="defaults">
              <DefaultsSettingsTab
                settings={settings}
                onSave={handleSave}
                isSaving={updateMutation.isPending}
                phoneNumbers={phoneNumbers}
                faxNumbers={faxNumbers}
                virtualFaxExtensions={virtualFaxExtensions}
                extensions={extensions}
                playlists={playlists}
                isLoadingReferenceData={isLoadingReferenceData}
                toast={toast}
                t={t}
              />
            </Tabs.Content>

            <Tabs.Content value="recording">
              <RecordingSettingsTab
                settings={settings}
                onSave={handleSave}
                isSaving={updateMutation.isPending}
                t={t}
              />
            </Tabs.Content>
          </Box>
        </Tabs.Root>
      </VStack>
    </Box>
  )
}
